/** Traffic datasets — which backend a map query should talk to, and what its numbers mean.
 *    mock  → /api/link/*      the Manhattan sample tables; values are an arbitrary "Volume".
 *    run   → /api/traffic/*   a real pipeline run (SUMO chain); values are V/C with LOS.
 *  They must never share a colour scale: 500 Volume and V/C 0.5 are different quantities.
 *  The dataset object carries `metric` so the renderer picks the right palette. */

import { losColor, kmhToMphExact, fetchMetros } from './networkEditor';
import { isLiveSimulation } from '../config';
import { visibleScenarios } from '../config/features';

export const MOCK_MANHATTAN = {
  id: 'mock-manhattan',
  kind: 'mock',
  label: 'Manhattan — sample data',
  sublabel: 'Illustrative values, not a pipeline run',
  metric: 'volume',
  bbox: [-74.0193, 40.7006, -73.8547, 40.8823],
  center: { longitude: -73.9352, latitude: 40.8006, zoom: 11 },
};

/** GET /api/traffic/runs → dataset descriptors, newest first, plus the mock. */
export async function fetchDatasets() {
  let runs = [];
  try {
    const res = await fetch('/api/traffic/runs');
    if (res.ok) runs = await res.json();
  } catch (e) {
    // A missing serving layer must not take the map down; the mock dataset
    // still works and the picker simply has one entry.
    console.error('fetchDatasets:', e);
  }

  // A scenario stored beside its baseline is opened from the panel that made it
  // (Congestion Pricing, School Zones, Network Editor), not picked as data.
  runs = runs.filter((r) => !r.compared_against);

  // A region with one run needs no scenario in its name; naming it anyway reads
  // as if there were others to choose from.
  const runsPerCbsa = runs.reduce((acc, r) => {
    acc[r.cbsa_id] = (acc[r.cbsa_id] || 0) + 1;
    return acc;
  }, {});

  const fromRuns = runs.map((r) => {
    const bbox = bboxOf(r.bbox);
    const region = shortRegion(r.region_name, r.cbsa_id);
    return {
      id: `run-${r.run_key}`,
      kind: 'run',
      runKey: r.run_key,
      runId: r.run_id,
      cbsa: r.cbsa_id,
      scenario: r.scenario,
      label: runsPerCbsa[r.cbsa_id] === 1
        ? region
        : `${region} — ${r.scenario_label || r.scenario}`,
      sublabel: hourLabel(r),
      metric: 'vc',
      hour: r.hour,
      // Every hour of the day the run has, with its own run key and totals.
      hours: (r.hours || []).map((h) => ({
        hour: h.hour,
        runKey: h.run_key,
        vehicles: h.vehicles,
        vmtMi: h.total_vmt_mi,
      })),
      weekday: r.weekday,
      demandScale: r.demand_scale,
      analysis: r.analysis,
      summary: {
        vehicles: r.vehicles,
        vmtMi: r.total_vmt_mi,
        congestedEdges: r.congested_edges,
        meanVc: r.mean_vc,
        edges: r.edges_with_data,
      },
      // { live, cp, edits } when the metro has pre-computed scenarios; live=false
      // means the edit service cannot hold it and only those scenarios exist.
      prebaked: r.prebaked || null,
      bbox,
      center: centerOf(bbox, r.focus),
    };
  });

  return [...fromRuns, MOCK_MANHATTAN];
}

/** A metro's pre-computed scenarios (charging zone, edits, headline numbers). */
export async function fetchPrebaked(cbsa) {
  const res = await fetch(`/api/traffic/prebaked?cbsa=${encodeURIComponent(cbsa)}`);
  if (!res.ok) throw new Error(`prebaked ${res.status}`);
  const pack = await res.json();
  if (pack.cp) pack.cp.scenarios = visibleScenarios(pack.cp.scenarios);
  return pack;
}

/** A metro's pricing scenarios over the day: the baseline at every hour, each
 *  scenario at the hours it charges, and totals where every charged hour it
 *  covers has a result. */
export async function fetchPrebakedDay(cbsa) {
  const res = await fetch(`/api/traffic/prebaked/day?cbsa=${encodeURIComponent(cbsa)}`);
  if (!res.ok) throw new Error(`prebaked day ${res.status}`);
  return res.json();
}

/** The edges one pre-computed edit changed, as GeoJSON. */
export async function fetchPrebakedTargets(cbsa, id) {
  const res = await fetch(
    `/api/traffic/prebaked/targets?cbsa=${encodeURIComponent(cbsa)}&id=${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`prebaked targets ${res.status}`);
  return res.json();
}

/** Roads whose V/C moved less than this are left off the pre-computed change maps; the
 *  legends say so, and the colour bands (networkEditor.js) start outside it. */
export const PREBAKED_MIN_DELTA = 0.02;

/** One pre-computed scenario of a run against another (the baseline unless
 *  `against` says otherwise), both at `hour`: totals plus every edge whose V/C
 *  moved by `minDelta` or more, largest change first. */
// The backend's ceiling (COMPARE_MAX), and the key its cache is warmed under: a cut by
// count draws some roads of a stretch and drops the next ones.
export async function fetchPrebakedComparison(cbsa, runId, scenario,
  { against = 'baseline', hour = null, minDelta = PREBAKED_MIN_DELTA, limit = 60000 } = {}) {
  const q = new URLSearchParams({ cbsa, runId, a: against, b: scenario,
                                  minDelta: String(minDelta), limit: String(limit) });
  if (hour !== null && hour !== undefined) q.set('hour', String(hour));
  const res = await fetch(`/api/traffic/compare?${q}`);
  if (!res.ok) throw new Error(`compare ${res.status}`);
  return res.json();
}

/**
 * Metros the edit service can hold, as datasets. Fetched after the produced runs
 * rather than with them: a slow service must not delay the map's own data.
 * A metro with produced runs is already in the picker under them.
 */
export async function fetchEditableDatasets(datasets) {
  if (!isLiveSimulation()) return [];
  const metros = await fetchMetros();
  const produced = new Set((datasets || []).map((d) => String(d.cbsa)));
  return metros
    .filter((m) => m.admissible && !produced.has(String(m.cbsa)))
    .map((m) => ({
      id: `metro-${m.cbsa}`,
      kind: 'editable',
      cbsa: String(m.cbsa),
      runId: m.run_id,
      label: m.name || `CBSA ${m.cbsa}`,
      sublabel: `Editable · no baseline layer${edgeNote(m)}`,
      metric: null,
      edges: m.edges,
      edgesExact: !!m.edges_exact,
      state: m.state,
      bbox: null,
      center: null,
    }));
}

function edgeNote(m) {
  if (!m.edges) return '';
  const n = Math.round(m.edges / 1000);
  return ` · ${m.edges_exact ? '' : '~'}${n}k roads`;
}

function shortRegion(name, cbsa) {
  if (!name) return `CBSA ${cbsa}`;
  return name.replace(/\s*\(.*\)\s*$/, ''); // 'Ithaca, NY (Tompkins County)' → 'Ithaca, NY'
}

function hourLabel(r) {
  if (r.hour == null) return r.analysis === 'microsim' ? 'Microsimulation' : '';
  const demand = r.demand_scale ? ` · demand ×${r.demand_scale}` : '';
  if ((r.hours || []).length > 1) return `${r.hours.length} hours${demand}`;
  return `${hourSpan(r.hour)}${demand}`;
}

/** '08:00–09:00' for hour 8. */
export function hourSpan(hour) {
  const h = String(hour).padStart(2, '0');
  const n = String((hour + 1) % 24).padStart(2, '0');
  return `${h}:00–${n}:00`;
}

/** The dataset as it is at one hour of the day: its run key for that hour. A
 *  dataset with one hour, or an hour it does not have, is returned as it is. */
export function atHour(dataset, hour) {
  const h = (dataset?.hours || []).find((x) => x.hour === hour);
  return h ? { ...dataset, runKey: h.runKey, hour: h.hour } : dataset;
}

function bboxOf(geojsonPolygon) {
  const ring = geojsonPolygon?.coordinates?.[0];
  if (!Array.isArray(ring) || ring.length < 4) return null;
  const xs = ring.map((p) => p[0]);
  const ys = ring.map((p) => p[1]);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

function centerOf(bbox, focus) {
  // Prefer the traffic-weighted centroid the API computes. A CBSA bbox centre
  // is a poor opening view: the bbox is mostly rural, so for Ithaca it lands
  // ~20 km from the city and the map opens on empty countryside.
  const c =
    focus && Number.isFinite(focus.lng) && Number.isFinite(focus.lat)
      ? { longitude: focus.lng, latitude: focus.lat }
      : bbox
      ? { longitude: (bbox[0] + bbox[2]) / 2, latitude: (bbox[1] + bbox[3]) / 2 }
      : null;
  if (!c) return null;

  // Zoom 12 rather than a bbox fit. Fitting a CBSA gives ~zoom 9, where the hex
  // grid exceeds MAX_HEX_CELLS and is suppressed — i.e. the default view would
  // be one where the main feature does not work. A pre-computed pack may ask
  // for a closer one.
  return { ...c, zoom: Number.isFinite(focus?.zoom) ? focus.zoom : 12 };
}

/**
 * Ask the right backend for per-cell aggregates.
 * `cells` is { hexId: [ [lng,lat], ... ] } (an outer ring).
 *
 * Returns { <hexId>: { value, metric, edgeCount, detail } } where `value` is
 * null when the cell contains no road at all — distinct from a road carrying
 * nothing, which is 0. The renderer must colour those differently.
 */
export async function fetchHexagonTraffic(dataset, cells) {
  const ids = Object.keys(cells);
  if (ids.length === 0) return {};

  if (dataset.kind === 'mock') {
    const IdToCoordinates = {};
    for (const id of ids) IdToCoordinates[id] = [cells[id]];
    const res = await fetch('/api/link/links/byHexagons/trafficDensity', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ IdToCoordinates }),
    });
    if (!res.ok) throw new Error(`byHexagons ${res.status}`);
    const raw = await res.json();
    const out = {};
    for (const id of ids) {
      const r = raw[id];
      const n = r?.linkCount || 0;
      out[id] = {
        metric: 'volume',
        edgeCount: n,
        value: n ? r.avgVolume : null,
        detail: n
          ? [
              ['Avg volume', Math.round(r.avgVolume)],
              ['Links', n],
            ]
          : [],
      };
    }
    return out;
  }

  const res = await fetch('/api/traffic/hexagons', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      runKey: dataset.runKey,
      cbsa: dataset.cbsa,
      scenario: dataset.scenario,
      hexagons: cells,
    }),
  });
  if (!res.ok) throw new Error(`traffic/hexagons ${res.status}`);
  const { hexagons } = await res.json();
  const out = {};
  for (const id of ids) {
    const r = hexagons[id] || { edgeCount: 0 };
    out[id] = {
      metric: 'vc',
      edgeCount: r.edgeCount,
      // Mean V/C is the cell's headline: max would let one slip road paint a
      // whole rural cell red.
      value: r.edgeCount ? r.meanVc : null,
      detail: r.edgeCount
        ? [
            ['Mean V/C', (r.meanVc ?? 0).toFixed(3)],
            ['Worst V/C', (r.maxVc ?? 0).toFixed(3)],
            ['Road segments', r.edgeCount],
            ['Vehicles', Math.round(r.vehCount ?? 0).toLocaleString()],
            ['Mean speed', `${kmhToMphExact(r.meanSpeedKmh).toFixed(1)} mph`],
            ['Vehicle-delay', `${Math.round((r.vehDelayS ?? 0) / 60).toLocaleString()} min`],
          ]
        : [],
    };
  }
  return out;
}

/** Per-edge / per-link detail inside a drawn polygon. */
export async function fetchPolygonTraffic(dataset, ring) {
  if (dataset.kind === 'mock') {
    const res = await fetch('/api/link/links/trafficDensity', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ IdToCoordinates: { poly: [ring] } }),
    });
    if (!res.ok) throw new Error(`trafficDensity ${res.status}`);
    return { ...(await res.json()), metric: 'volume' };
  }
  const res = await fetch('/api/traffic/links', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      runKey: dataset.runKey,
      cbsa: dataset.cbsa,
      scenario: dataset.scenario,
      polygon: ring,
    }),
  });
  if (!res.ok) throw new Error(`traffic/links ${res.status}`);
  return { ...(await res.json()), metric: 'vc' };
}

/** Exactly the roads `edgeIds` names, with one stored run's V/C at `hour`. */
export async function fetchEdgeTraffic(cbsa, runId, scenario, hour, edgeIds) {
  const res = await fetch('/api/traffic/links', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cbsa, runId, scenario, hour, edgeIds }),
  });
  if (!res.ok) throw new Error(`traffic/links ${res.status}`);
  return res.json();
}

/** Route annotated with the modelled conditions along it (way_id join). */
export async function fetchRouteTraffic(dataset, startCoord, endCoord, costing) {
  const res = await fetch('/api/traffic/route', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      runKey: dataset.runKey,
      cbsa: dataset.cbsa,
      scenario: dataset.scenario,
      startCoord,
      endCoord,
      costing,
    }),
  });
  if (!res.ok) throw new Error(`traffic/route ${res.status}`);
  return res.json();
}

// --- Scenarios -----------------------------------------------------------

// Nothing here reads a second stored run any more: a region ships one baseline and
// every policy is run live. `/api/traffic/compare` still exists for a region that
// has two, and `scenario/run` does the per-edge diff for a live one.

/**
 * Run a scenario on this region for real: congestion pricing on a drawn zone
 * (~7 s, no netconvert) or school zones at a given radius and speed cap (~14 s,
 * the network is recompiled). Not a lookup — the caller should show progress.
 */
export async function runScenario(dataset, opts) {
  const res = await fetch('/api/traffic/scenario/run', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cbsa: dataset.cbsa, minDelta: 0.01, ...opts }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `scenario/run ${res.status}`);
  }
  return res.json();
}

/** The schools a school-zone run would be built around, for the region's own list. */
export async function fetchSchools(cbsa) {
  const q = cbsa ? `?cbsa=${encodeURIComponent(cbsa)}` : '';
  const res = await fetch(`/api/demo/schools${q}`);
  if (!res.ok) throw new Error(`demo/schools ${res.status}`);
  return res.json();
}

// --- Colour --------------------------------------------------------------

const NO_ROAD = [90, 90, 90, 60];

/**
 * V/C reuses the Network Editor's LOS palette so a hexagon and a road segment
 * mean the same thing by colour. Volume keeps its own ramp — the original
 * thresholds, which were fitted to the Manhattan sample and say nothing about
 * anywhere else.
 */
export function hexColor(cell) {
  if (!cell || cell.value == null) return NO_ROAD;
  if (cell.metric === 'vc') return losColor(cell.value, 190);
  const v = cell.value;
  if (v > 800) return [255, 0, 0, 180];
  if (v > 600) return [255, 165, 0, 180];
  if (v > 400) return [255, 255, 0, 180];
  if (v > 200) return [0, 255, 0, 180];
  return [0, 128, 255, 180];
}

export const VOLUME_LEGEND = [
  { label: '> 800', color: [255, 0, 0] },
  { label: '600–800', color: [255, 165, 0] },
  { label: '400–600', color: [255, 255, 0] },
  { label: '200–400', color: [0, 255, 0] },
  { label: '< 200', color: [0, 128, 255] },
];
